/**
 * Central registry of TanStack Query keys used across all surfaces.
 * ALL queries should use these keys so cross-module invalidation works.
 *
 * Mutation flows should:
 *   1. Use `withRollback()` or manually cancelQueries + setQueryData
 *   2. Call the appropriate invalidate function onSettled
 */
import type { QueryClient } from "@tanstack/react-query";

// ── Domain Keys ──────────────────────────────────────────────────────────

export const queryKeys = {
  // Tours & Events
  tours: ["tours"] as const,
  dashboardTours: ["dashboard-tours"] as const,
  dashboardEvents: ["dashboard-events"] as const,
  allEventsAndTours: ["all-events-and-tours"] as const,
  calendarEvents: ["calendar-events"] as const,
  event: (id: string | undefined) => ["event", id] as const,
  eventDetail: (id: string | undefined) => ["event-detail", id] as const,
  tourGigs: (tourId: string | undefined) => ["tour-gigs", tourId] as const,
  tourExpenses: (tourId: string | undefined) => ["tour-expenses", tourId] as const,
  tourRevenue: (tourId: string | undefined) => ["tour-revenue", tourId] as const,
  tourTransportation: (tourId: string | undefined) => ["tour-transportation", tourId] as const,
  tourAccommodations: (tourId: string | undefined) => ["tour-accommodations", tourId] as const,
  tourBalance: (tourId: string | undefined) => ["tour-balance", tourId] as const,
  tourDetails: (tourId: string | undefined) => ["tour-details", tourId] as const,
  tourOptimizationStatus: (tourId: string | undefined) => ["tour-optimization-status", tourId] as const,
  tourContacts: (tourId: string | undefined) => ["tour-contacts", tourId] as const,

  // Venues
  venues: ["venues"] as const,
  venue: (id: string | undefined) => ["venue", id] as const,
  venueEquipment: (venueId: string | undefined) => ["venue-equipment", venueId] as const,
  venueSpaces: (venueId: string | undefined) => ["venue-spaces", venueId] as const,
  venueEvents: (venueId: string | undefined) => ["venue-events", venueId] as const,

  // Music
  songs: ["songs"] as const,
  song: (id: string | undefined) => ["song", id] as const,
  playlists: ["playlists"] as const,
  playlist: (id: string | undefined) => ["playlist", id] as const,
  setlists: ["setlists"] as const,
  setlist: (id: string | undefined) => ["setlist", id] as const,

  // Equipment
  equipmentList: ["equipment-list"] as const,
  equipment: (id: string | undefined) => ["equipment", id] as const,
  equipmentAllocations: (id: string | undefined) => ["equipment-allocations", id] as const,
  equipmentAvailability: (id: string | undefined) => ["equipment-availability", id] as const,

  // Riders
  riders: ["riders"] as const,
  rider: (id: string | undefined) => ["rider", id] as const,

  // Accommodations
  accommodations: ["accommodations"] as const,
  accommodation: (id: string | undefined) => ["accommodation", id] as const,

  // Ensembles
  ensembles: ["ensembles"] as const,
  ensemble: (id: string | undefined) => ["ensemble", id] as const,
  ensembleContacts: (id: string | undefined) => ["ensemble-contacts", id] as const,
  ensembleResolvedSplits: (id: string | undefined) => ["ensemble-resolved-splits", id] as const,
  ensembleSplitTemplates: (id: string | undefined) => ["ensemble-split-templates", id] as const,
  ensembleRehearsals: (id: string | undefined) => ["ensemble-rehearsals", id] as const,
  ensembleRoles: (id: string | undefined) => ["ensemble-roles", id] as const,
  ensembleAnnouncements: (id: string | undefined) => ["ensemble-announcements", id] as const,
  ensembleMembers: (id: string | undefined) => ["ensemble-members", id] as const,
  ensemblePlaylists: (id: string | undefined) => ["ensemble-playlists", id] as const,
  ensembleDocuments: (id: string | undefined) => ["ensemble-documents", id] as const,
  ensembleAttendance: (ensembleId: string | undefined, rehearsalId: string | undefined) => ["ensemble-attendance", ensembleId, rehearsalId] as const,

  // Accounting
  accountingContactBalances: ["accounting-contact-balances"] as const,
  accountingPendingPayments: ["accounting-pending-payments"] as const,
  accountingRecentSettlements: ["accounting-recent-settlements"] as const,

  // Financial
  financialOverview: (period: string) => ["financial-overview", period] as const,
  tourFinances: ["tour-finances"] as const,
  eventFinances: ["event-finances"] as const,
  vatSummary: ["vat-summary"] as const,

  // Merchandise
  merchStats: ["merch-stats"] as const,
  merchInventory: ["merch-inventory"] as const,
  merchSales: ["merch-sales"] as const,
  merchInsights: ["merch-insights"] as const,
};

// ── Rollback Utility ─────────────────────────────────────────────────────

/**
 * Helper for the onMutate step of optimistic updates.
 * Cancels in-flight queries, saves previous state for rollback.
 *
 * Usage:
 *   useMutation({
 *     mutationFn: ...,
 *     ...withRollback(queryClient, queryKey),
 *     onSuccess: (data) => { /* UI toasts, state resets *\/ },
 *   })
 */
export function withRollback<TData = unknown>(
  queryClient: QueryClient,
  queryKey: unknown[],
  updater?: (old: TData | undefined) => TData | undefined,
) {
  return {
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<TData>(queryKey);
      if (updater) {
        queryClient.setQueryData<TData>(queryKey, (old) => updater(old));
      }
      return { previous };
    },
    onError: (_err: unknown, _vars: unknown, context: { previous?: TData } | undefined) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
  };
}

// ── Domain Invalidation Helpers ───────────────────────────────────────────

/**
 * Invalidate all venue queries (list + detail).
 */
export function invalidateVenueQueries(queryClient: QueryClient, venueId?: string): Promise<void> {
  const promises: Promise<unknown>[] = [
    queryClient.invalidateQueries({ queryKey: queryKeys.venues }),
  ];
  if (venueId) {
    promises.push(
      queryClient.invalidateQueries({ queryKey: queryKeys.venue(venueId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.venueEquipment(venueId) }),
    );
  }
  return Promise.all(promises).then(() => undefined);
}

/**
 * Invalidate all music queries (songs, playlists, setlists).
 */
export function invalidateMusicQueries(
  queryClient: QueryClient,
  scope?: { songId?: string; playlistId?: string; setlistId?: string },
): Promise<void> {
  const promises: Promise<unknown>[] = [
    queryClient.invalidateQueries({ queryKey: queryKeys.songs }),
    queryClient.invalidateQueries({ queryKey: queryKeys.playlists }),
    queryClient.invalidateQueries({ queryKey: queryKeys.setlists }),
  ];
  if (scope?.songId) promises.push(queryClient.invalidateQueries({ queryKey: queryKeys.song(scope.songId) }));
  if (scope?.playlistId) promises.push(queryClient.invalidateQueries({ queryKey: queryKeys.playlist(scope.playlistId) }));
  if (scope?.setlistId) promises.push(queryClient.invalidateQueries({ queryKey: queryKeys.setlist(scope.setlistId) }));
  return Promise.all(promises).then(() => undefined);
}

/**
 * Invalidate all rider queries (list + detail).
 */
export function invalidateRiderQueries(queryClient: QueryClient, riderId?: string): Promise<void> {
  const promises: Promise<unknown>[] = [
    queryClient.invalidateQueries({ queryKey: queryKeys.riders }),
  ];
  if (riderId) promises.push(queryClient.invalidateQueries({ queryKey: queryKeys.rider(riderId) }));
  return Promise.all(promises).then(() => undefined);
}

/**
 * Invalidate equipment queries.
 */
export function invalidateEquipmentQueries(queryClient: QueryClient, equipmentId?: string): Promise<void> {
  const promises: Promise<unknown>[] = [
    queryClient.invalidateQueries({ queryKey: queryKeys.equipmentList }),
  ];
  if (equipmentId) {
    promises.push(
      queryClient.invalidateQueries({ queryKey: queryKeys.equipment(equipmentId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.equipmentAllocations(equipmentId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.equipmentAvailability(equipmentId) }),
    );
  }
  return Promise.all(promises).then(() => undefined);
}

export interface InvalidateScope {
  tourId?: string;
  eventId?: string;
}

/**
 * Invalidate every query that could be affected by a tour, event, or gig
 * create / update / delete. Call this from mutation onSuccess handlers.
 *
 * - Always invalidates the cross-view list caches.
 * - If tourId is provided, also invalidates that tour's gigs.
 * - If eventId is provided, also invalidates that event's detail.
 */
/**
 * Invalidate all accounting queries.
 */
export function invalidateAccountingQueries(queryClient: QueryClient): Promise<void> {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: queryKeys.accountingContactBalances }),
    queryClient.invalidateQueries({ queryKey: queryKeys.accountingPendingPayments }),
    queryClient.invalidateQueries({ queryKey: queryKeys.accountingRecentSettlements }),
  ]).then(() => undefined);
}

export function invalidateTourAndEventQueries(
  queryClient: QueryClient,
  scope: InvalidateScope = {},
): Promise<void> {
  const promises: Promise<unknown>[] = [
    queryClient.invalidateQueries({ queryKey: queryKeys.tours }),
    queryClient.invalidateQueries({ queryKey: queryKeys.dashboardTours }),
    queryClient.invalidateQueries({ queryKey: queryKeys.dashboardEvents }),
    queryClient.invalidateQueries({ queryKey: queryKeys.allEventsAndTours }),
  ];
  if (scope.tourId) {
    promises.push(
      queryClient.invalidateQueries({ queryKey: queryKeys.tourGigs(scope.tourId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.tourExpenses(scope.tourId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.tourRevenue(scope.tourId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.tourTransportation(scope.tourId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.tourAccommodations(scope.tourId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.tourBalance(scope.tourId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.tourDetails(scope.tourId) }),
    );
    // Invalidate ALL event queries since we don't know which events reference this tour
    promises.push(
      queryClient.invalidateQueries({ queryKey: ["event"] }),
      queryClient.invalidateQueries({ queryKey: ["event-detail"] }),
    );
  }
  if (scope.eventId) {
    promises.push(
      queryClient.invalidateQueries({ queryKey: queryKeys.event(scope.eventId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.eventDetail(scope.eventId) }),
    );
  }
  return Promise.all(promises).then(() => undefined);
}

/**
 * Invalidate all merchandise queries (stats, inventory, sales, insights).
 */
export function invalidateMerchQueries(queryClient: QueryClient): Promise<void> {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: queryKeys.merchStats }),
    queryClient.invalidateQueries({ queryKey: queryKeys.merchInventory }),
    queryClient.invalidateQueries({ queryKey: queryKeys.merchSales }),
    queryClient.invalidateQueries({ queryKey: queryKeys.merchInsights }),
  ]).then(() => undefined);
}
